import React, { useState, useEffect } from 'react';
import { useAppContext } from '../contexts/AppContext';
import EpochTimeline from '../components/EpochComparison/EpochTimeline';
import RPNEvolutionChart from '../components/EpochComparison/RPNEvolutionChart';
import CostEvolutionChart from '../components/EpochComparison/CostEvolutionChart';
import EpochComparisonTable from '../components/EpochComparison/EpochComparisonTable';
import { RefreshCw, Download, GitBranch, AlertCircle, Upload, Trash2, CheckSquare, AlertTriangle, Zap, Sparkles, TrendingUp, Box } from 'lucide-react';
import axios from 'axios';
import ActiveProjectBadge from '../components/ActiveProjectBadge';
import { ModuleInfoModal } from '../components/ModuleInfoModal';
import CadViewerModal from '../components/CadViewerModal';
import { API_BASE_URL } from '../utils/apiConfig';

import { normalizeProjectName, extractProjectName } from '../utils/validation';

// Define types locally for now if not in types.ts
interface EpochSummary {
    epochNumber: number;
    epochName: string;
    epochDate: string;
    totalRPN: number;
    avgRPN: number;
    totalCost: number;
    phase: string;
    status: string;
    projectName?: string;
}

const EpochManager: React.FC = () => {
    const { allData, addEpoch, deleteEpoch, activeProject, importProjectData, projectList, exportProject } = useAppContext();
    const [epochs, setEpochs] = useState<EpochSummary[]>([]);
    const [loading, setLoading] = useState(true);
    const [currentEpochId, setCurrentEpochId] = useState<number>(0);
    const [comparisonData, setComparisonData] = useState<any>(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showFileModal, setShowFileModal] = useState(false);
    const [fileList, setFileList] = useState<string[]>([]);

    // Gap 3 & 5: Synthesis sessions state
    const [showSynthesisModal, setShowSynthesisModal] = useState(false);
    const [synthesisSessions, setSynthesisSessions] = useState<any[]>([]);
    const [savingSeed, setSavingSeed] = useState<string | null>(null);
    const [showAuditModal, setShowAuditModal] = useState(false);
    const [auditReportMd, setAuditReportMd] = useState<string>('');
    const [auditReportTitle, setAuditReportTitle] = useState<string>('');
    
    // New 3D CAD WebGL Viewer State
    const [activeCadSynthesisId, setActiveCadSynthesisId] = useState<{id: string, name: string} | null>(null);

    // Active Evolutionary Run Telemetry (Persistence check)
    const [activeRun, setActiveRun] = useState<any>(null);

    useEffect(() => {
        const checkActiveRun = async () => {
            try {
                const res = await axios.get(`${API_BASE_URL}/api/v1/genesis/latest-synthesis`);
                if (res.data && (res.data.is_running || res.data.status === 'In Progress' || res.data.status === 'Running')) {
                    setActiveRun(res.data);
                } else if (res.data && !res.data.is_running && activeRun) {
                    // It finished
                    setActiveRun(null);
                }
            } catch (e) {
                // Ignore API errors gracefully
            }
        };
        // Initial check immediately
        checkActiveRun();
        // Poll every 4s
        const interval = setInterval(checkActiveRun, 4000);
        return () => clearInterval(interval);
    }, [activeRun]);

    // New Epoch Form State
    const [newEpochName, setNewEpochName] = useState('');
    const [newEpochPhase, setNewEpochPhase] = useState('Design');

    const getEpochName = (e: any) => {
        // Priority 1: Explicit name from generator (e.g. "Magnus Genesis Epoch 1")
        if (e.epochName && e.epochName !== 'Iteration ' + e.epochNumber && !e.epochName.startsWith('Iteration')) return e.epochName;

        // Priority 2: Constructed Name (Product + Phase)
        const product = "Milk Frother"; // Default for current run
        const phase = e.phase || (e.epochNumber <= 2 ? 'Concept' : e.epochNumber <= 5 ? 'Design' : 'Validation');
        const trigger = e.trigger || (e.epochNumber === 1 ? 'Genesis' : 'Evolution');

        return `${product} - ${phase} (${trigger})`;
    };

    // Extract unique Projects for Bulk Management (Local only - based on loaded history)
    const projects = React.useMemo(() => {
        return projectList.map(p => ({
            name: p.name,
            count: p.epochIds.length,
            ids: p.epochIds
        }));
    }, [projectList]);

    const handleDeleteProject = async (name: string, ids: number[]) => {
        if (confirm(`⚠ DESTRUCTIVE ACTION\n\nAre you sure you want to delete project "${name}"?\nThis will permanently remove ALL ${ids.length} epochs associated with it.\n\nType 'DELETE' to confirm.`)) {
            // Simple confirmation check, actually I can't check input easily in confirm(), so just standard ok/cancel
            // Using standard confirm twice for safety
            if (confirm(`Double Check: Really delete ${ids.length} epochs for ${name}?`)) {
                try {
                    // 1. Delete Epochs locally
                    const remainingEpochs = (allData.iterationHistory || []).filter((e: any) => !ids.includes(e.epochNumber || e.iteration));

                    // 2. Delete Associated FMEA Items locally
                    const remainingItems = (allData.fmeaItems || []).filter(item => !item.epochId || !ids.includes(item.epochId));

                    // 3. Update Global State & Persistence
                    // 3. Update Global State
                    const remainingData = {
                        ...allData,
                        iterationHistory: remainingEpochs,
                        fmeaItems: remainingItems
                    };

                    // 4. Use import with replace mode to refresh context
                    await importProjectData(remainingData, true);

                } catch (e) {
                    alert("Error deleting some epochs. Check console.");
                    console.error(e);
                }
            }
        }
    };

    const clearHistory = () => {
        if (confirm("⚠️ Are you sure you want to clear all local epoch history? This cannot be undone.")) {
            localStorage.removeItem('magnus_dfr_data');
            window.location.reload();
        }
    };

    useEffect(() => {
        if (allData.iterationHistory && allData.iterationHistory.length > 0) {
            let filteredHistory = allData.iterationHistory;

            // Filter by Active Project if set
            if (activeProject && activeProject !== 'all') {
                const activeProjectNormalized = normalizeProjectName(activeProject);
                filteredHistory = filteredHistory.filter((e: any) => {
                    // 1. Direct project tag
                    if (e.projectName) {
                        return normalizeProjectName(e.projectName) === activeProjectNormalized;
                    }

                    // 2. Fallback to epochName parsing
                    const epochProjectName = extractProjectName(e.epochName || '');
                    return normalizeProjectName(epochProjectName) === activeProjectNormalized;
                });
            }

            const mappedEpochs: EpochSummary[] = filteredHistory.map((e: any) => ({
                epochNumber: e.iteration || e.epochNumber || 0,
                epochName: getEpochName(e),
                epochDate: e.date || e.epochDate || new Date().toISOString().split('T')[0],
                totalRPN: e.total_score || e.totalRPN || 0,
                avgRPN: e.average_score || e.avgRPN || 0,
                totalCost: e.totalCost || 0,
                phase: e.phase || ((e.iteration || e.epochNumber) <= 2 ? 'Concept' : 'Design'), // Better default
                status: e.status || 'completed',
                projectName: e.projectName || extractProjectName(getEpochName(e))
            }));

            // DE-DUPLICATE EPOCHS by epochNumber
            // We use a Map to keep the LAST duplicate encountered (assuming newly appended is fresher)
            const uniqueEpochsMap = new Map<number, EpochSummary>();
            mappedEpochs.forEach(epoch => {
                uniqueEpochsMap.set(epoch.epochNumber, epoch);
            });
            const uniqueEpochs = Array.from(uniqueEpochsMap.values());

            // Sort by epoch number
            uniqueEpochs.sort((a, b) => a.epochNumber - b.epochNumber);

            setEpochs(uniqueEpochs);
            if (uniqueEpochs.length > 0) {
                // If current selection is not in list, select the last one
                if (!uniqueEpochs.some(e => e.epochNumber === currentEpochId)) {
                    setCurrentEpochId(Math.max(...uniqueEpochs.map(e => e.epochNumber)));
                }
            } else {
                setCurrentEpochId(0);
            }


            setComparisonData({
                epochs: uniqueEpochs.map(e => ({
                    ...e,
                    timestamp: e.epochDate && !e.epochDate.includes('T') ? `${e.epochDate}T12:00:00` : e.epochDate,
                    criticalItemCount: e.totalRPN > 300 ? 5 : 0,
                    changes: []
                }))
            });

            setLoading(false);
        } else {
            // Fallback if data is missing
            setEpochs([]);
            setComparisonData(null);
            setLoading(false);
        }
    }, [allData, activeProject]);

    const handleCreateEpoch = async () => {
        const nextId = epochs.length > 0 ? Math.max(...epochs.map(e => e.epochNumber)) + 1 : 1;

        // In a real app, this would recalculate RPN based on current FMEA state
        // For this demo, we'll simulate a 10% improvement
        const lastRPN = epochs.length > 0 ? epochs[epochs.length - 1].totalRPN : 500;
        const newRPN = Math.floor(lastRPN * 0.9);

        const newEpoch = {
            iteration: nextId, // aligning with seed data format
            epochNumber: nextId,
            name: newEpochName,
            epochName: newEpochName,
            date: new Date().toISOString().split('T')[0],
            epochDate: new Date().toISOString().split('T')[0],
            total_score: newRPN,
            totalRPN: newRPN,
            average_score: Math.floor(newRPN / 10),
            avgRPN: Math.floor(newRPN / 10),
            totalCost: 15000,
            phase: newEpochPhase,
            status: 'current'
        };

        if (addEpoch) {
            await addEpoch(newEpoch);
            setShowCreateModal(false);
            setNewEpochName('');
            alert(`✅ Epoch ${nextId} Created!\n\nName: ${newEpoch.name}\nPhase: ${newEpoch.phase}\nTarget RPN: ${newEpoch.totalRPN}`);
        }
    };

    const handleEpochClick = (id: number) => {
        setCurrentEpochId(id);
    };

    const handleSyncToBackend = async () => {
        try {
            setLoading(true);
            const filename = allData.projectMetadata?.name ? `${allData.projectMetadata.name}.json` : undefined;

            const response = await axios.post(`${API_BASE_URL}/api/visualization/sync-epochs`, {
                iterationHistory: allData.iterationHistory,
                fmeaItems: allData.fmeaItems,
                pDiagramList: allData.pDiagramList,
                customerRequirements: allData.customerRequirements,
                projectMetadata: allData.projectMetadata,
                projectContext: allData.projectContext,
                filename: filename
            });

            if (response.data.success) {
                alert(`✅ Synced ${response.data.totalEpochs} epochs to Magnus Insight!`);
            }
        } catch (error) {
            console.error(error);
            alert('Failed to sync to backend.');
        } finally {
            setLoading(false);
        }
    };

    const importGeneratedEpochs = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_BASE_URL}/api/visualization/epoch-files`);
            setFileList(res.data);
            setShowFileModal(true);
        } catch {
            alert('Failed to list files');
        } finally {
            setLoading(false);
        }
    };

    const loadFile = async (filename: string) => {
        if (!confirm(`Replace current workspace with ${filename}? Unsaved changes will be lost.`)) return;
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/api/visualization/full-epochs?filename=${encodeURIComponent(filename)}&t=${Date.now()}`);
            const history = response.data.iterationHistory || response.data.epochs;

            if (history && history.length > 0) {
                const generatedEpochs = history;
                const appData: any = {
                    ...allData,
                    iterationHistory: generatedEpochs,
                    projectMetadata: response.data.projectMetadata || allData.projectMetadata,
                    projectContext: response.data.projectContext || allData.projectContext,
                    customerRequirements: response.data.customerRequirements || allData.customerRequirements,
                    pDiagramList: response.data.pDiagramList || response.data.pDiagrams || allData.pDiagramList,
                    fmeaItems: (response.data.fmeaItems && response.data.fmeaItems.length > 0)
                        ? response.data.fmeaItems
                        : (generatedEpochs[generatedEpochs.length - 1].fmeaItems || allData.fmeaItems),
                };

                // Use the new open strategy (instant load, no reload)
                await importProjectData(appData, true);
            } else {
                alert('File appears empty or invalid.');
            }
        } catch (e) {
            console.error(e);
            alert("Load failed");
        } finally {
            setLoading(false);
            setShowFileModal(false);
        }
    };

    const deleteFile = async (filename: string) => {
        if (!confirm(`Permanently delete "${filename}"?`)) return;
        try {
            await axios.delete(`${API_BASE_URL}/api/visualization/delete-file?filename=${encodeURIComponent(filename)}`);
            setFileList(prev => prev.filter(f => f !== filename));
        } catch {
            alert("Failed to delete file.");
        }
    };

    // ── GAP 5: Download synthesis brief as JSON ──────────────────────────────
    const downloadBrief = (synthesisId: string, projectName: string) => {
        const url = `${API_BASE_URL}/api/v1/genesis/synthesis/${synthesisId}/download-brief`;
        const a = document.createElement('a');
        a.href = url;
        a.download = `magnus_synthesis_${synthesisId}.json`;
        a.click();
    };

    // ── GAP 5: View/Download audit report markdown ───────────────────────────
    const viewAuditReport = async (synthesisId: string, projectName: string, download = false) => {
        try {
            if (download) {
                window.open(`${API_BASE_URL}/api/v1/genesis/synthesis/${synthesisId}/audit-report?download=true`, '_blank');
                return;
            }
            const res = await axios.get(`${API_BASE_URL}/api/v1/genesis/synthesis/${synthesisId}/audit-report`);
            setAuditReportMd(res.data);
            setAuditReportTitle(`📊 Audit: ${projectName}`);
            setShowAuditModal(true);
        } catch {
            alert('Audit report not available for this synthesis.');
        }
    };

    // ── View full synthesis report (Mermaid flowcharts + all V4 data) ────────────
    const viewSynthesisReport = async (synthesisId: string, projectName: string, download = false) => {
        try {
            if (download) {
                window.open(`${API_BASE_URL}/api/v1/genesis/synthesis/${synthesisId}/synthesis-report?download=true`, '_blank');
                return;
            }
            const res = await axios.get(`${API_BASE_URL}/api/v1/genesis/synthesis/${synthesisId}/synthesis-report`);
            setAuditReportMd(res.data);
            setAuditReportTitle(`🧠 Full Synthesis Report: ${projectName}`);
            setShowAuditModal(true);
        } catch {
            alert('Synthesis report not available. Try running a V4 synthesis first.');
        }
    };

    // ── GAP 3: Open synthesis sessions modal ────────────────────────────────
    const openSynthesisSessions = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_BASE_URL}/api/v1/genesis/synthesis-list`);
            setSynthesisSessions(res.data.sessions || []);
            setShowSynthesisModal(true);
        } catch {
            alert('Failed to load synthesis sessions.');
        } finally {
            setLoading(false);
        }
    };

    // ── GAP 3: Save synthesis as epoch seed ─────────────────────────────────
    const saveAsSeed = async (synthesisId: string, projectName: string) => {
        setSavingSeed(synthesisId);
        try {
            const seedName = prompt(`Name this seed epoch:`, `Seed: ${projectName}`);
            if (!seedName) { setSavingSeed(null); return; }
            const res = await axios.post(`${API_BASE_URL}/api/v1/genesis/save-as-seed`, {
                synthesis_id: synthesisId,
                seed_name: seedName
            });
            alert(`✅ Seed saved!\n\nEpoch #${res.data.epoch_number}: "${res.data.epoch_name}"\n\n${res.data.seed_note}`);
        } catch {
            alert('Failed to save seed. Synthesis may have expired from memory.');
        } finally {
            setSavingSeed(null);
        }
    };

    return (
        <div className="space-y-8 animate-in fade-in duration-500 max-w-[1400px] mx-auto p-4 pb-32">
            <div className="flex justify-between items-center bg-white/80 border border-zinc-200/60 p-8 rounded-[2.5rem] shadow-xl backdrop-blur-xl">
                <div className="space-y-2">
                    <h2 className="text-4xl font-black text-zinc-900 tracking-tighter flex items-center gap-4 uppercase tracking-[0.05em]">
                         <div className="p-3 bg-blue-600 text-white rounded-2xl shadow-xl shadow-blue-500/40"><GitBranch size={28} /></div>
                        EPOCH MANAGER <span className="text-blue-500 font-black text-2xl">v2.5</span>
                    </h2>
                    <p className="text-blue-600/80 text-[10px] font-black tracking-[0.4em] uppercase">Version Control & Convergence Analysis // {activeRun?.project_name || activeRun?.id?.slice(0,14) || 'No Active Run'}</p>
                </div>
                <div className="flex gap-2 items-center">
                    <ModuleInfoModal
                        title="Epoch Manager"
                        description={`Track your product's evolution across design iterations (epochs)${activeProject !== 'all' ? ` for ${activeProject}` : ''}. Each epoch represents a snapshot of the FMEA state, allowing you to monitor risk reduction, cost impact, and design changes over time.`}
                        description2={`Currently tracking ${epochs.length} iteration${epochs.length !== 1 ? 's' : ''} across ${projects.length} project${projects.length !== 1 ? 's' : ''}. Epochs are version control for your reliability analysis - compare historical data, track improvements, and validate that design changes reduce overall system risk.`}
                        benefits={[
                            `Visual timeline showing ${epochs.length} iterations with RPN and cost evolution`,
                            "Compare any two epochs side-by-side to see exactly what changed",
                            "Track risk reduction trends: confirm design improvements are working",
                            `Import/export epoch data for backup, sharing, or batch generation`,
                            "Bulk project management: delete entire project histories when needed",
                            "Sync to Magnus Insight for advanced multi-project analytics"
                        ]}
                        usageInstructions={[
                            "Review the Version History timeline to see all recorded iterations",
                            "Click any epoch bubble to select it and view detailed metrics",
                            "Use RPN Evolution and Cost Evolution charts to spot trends and validate improvements",
                            "Click 'New Epoch' to create a new iteration snapshot of current FMEA data",
                            "Use 'Saved Sessions' to import generated epochs from Magnus Genesis batches",
                            "Project Maintenance section: bulk delete old/test project data to keep workspace clean"
                        ]}
                    />
                    <ActiveProjectBadge showStandard={false} />
                    <button
                        onClick={clearHistory}
                        className="flex items-center gap-2 px-3 py-2 bg-red-500/10 text-red-500 border border-red-500/20 rounded hover:bg-red-500/20 text-sm shadow-sm"
                        title="Clear all local history"
                    >
                        Clear History
                    </button>
                    <button
                        onClick={handleSyncToBackend}
                        className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm shadow-sm"
                        title="Sync current data to Magnus Insight"
                    >
                        <Upload size={16} /> Sync to Insight
                    </button>
                    <button
                        onClick={importGeneratedEpochs}
                        className="flex items-center gap-2 px-3 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 text-sm shadow-sm"
                        title="Import from Python Generator"
                    >
                        <RefreshCw size={16} /> Saved Sessions
                    </button>
                    {/* GAP 3 & 5: Synthesis Sessions button */}
                    <button
                        onClick={openSynthesisSessions}
                        className="flex items-center gap-2 px-3 py-2 bg-emerald-600 text-white rounded hover:bg-emerald-700 text-sm shadow-sm"
                        title="View synthesis runs, download reports, save seeds"
                    >
                        <Download size={16} /> Synthesis Library
                    </button>
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm shadow-sm"
                    >
                        <AlertCircle size={16} /> New Epoch
                    </button>
                    <button
                        onClick={async () => {
                            const url = await exportProject('pdf');
                            window.open(url, '_blank');
                        }}
                        className="flex items-center gap-2 px-4 py-2.5 bg-zinc-900 text-white rounded-xl hover:bg-black text-[11px] font-black uppercase tracking-wider shadow-lg hover:shadow-xl transition-all"
                    >
                        <Download size={16} /> Export Report
                    </button>
                </div>
            </div>

            {/* ACTIVE RUN PROGRESS BANNER */}
            {activeRun && (
                <div className="p-10 rounded-[2.5rem] bg-blue-600/5 border border-blue-500/30 relative overflow-hidden group shadow-2xl backdrop-blur-sm">
                    <div className="flex flex-col md:flex-row items-start md:items-center gap-8 relative z-10">
                        <div className="flex items-center gap-8">
                            <div className="w-20 h-20 bg-blue-600 text-white rounded-3xl flex items-center justify-center shadow-xl shadow-blue-600/30 shrink-0">
                                <RefreshCw size={36} className="animate-spin" />
                            </div>
                            <div className="space-y-2">
                                <h3 className="text-2xl font-black text-blue-600 tracking-widest uppercase mb-1 drop-shadow-sm">Evolutionary Kernel: Epoch {activeRun.current_epoch || '-'}/{activeRun.total_epochs || 7}</h3>
                                <p className="text-sm font-bold text-zinc-600">{activeRun.status || "Orchestrating..."}</p>
                                <div className="flex items-center gap-4 mt-2">
                                    <div className="w-64 h-2 bg-blue-900/10 overflow-hidden rounded-full border border-blue-500/20">
                                        <div className="h-full bg-blue-500 rounded-full transition-all duration-1000 relative overflow-hidden" style={{ width: `${Math.max(5, ((activeRun.current_epoch || 0) / (activeRun.total_epochs || 7)) * 100)}%` }}>
                                            <div className="absolute inset-0 bg-white/20 w-full h-full animate-[shimmer_1s_infinite]" />
                                        </div>
                                    </div>
                                    <span className="text-xs font-black text-blue-600 tabular-nums">{Math.round(((activeRun.current_epoch || 0) / (activeRun.total_epochs || 7)) * 100)}%</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Project Management (Bulk Delete) */}
            {projects.length > 0 && (
                <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-4 mb-4">
                    <h2 className="text-sm font-semibold text-red-600 uppercase tracking-wider mb-2 flex items-center gap-2">
                        <Trash2 size={16} /> Project Maintenance
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {projects.map(p => (
                            <div key={p.name} className="flex items-center justify-between gap-3 bg-white/50 px-3 py-3 rounded border border-red-100 shadow-sm">
                                <div>
                                    <span className="font-bold block text-gray-800">{p.name === "Uncategorized" ? "Legacy / Unnamed" : p.name}</span>
                                    <span className="text-xs text-gray-500">{p.count} Epochs Recorded</span>
                                </div>
                                <button
                                    onClick={() => handleDeleteProject(p.name, p.ids)}
                                    className="bg-red-100 hover:bg-red-200 text-red-700 border border-red-300 px-3 py-1.5 rounded text-xs font-bold transition-colors"
                                >
                                    DELETE ALL
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Timeline */}
            <div className="bg-card border border-border rounded-lg p-4 shadow-sm">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-sm font-semibold text-text-secondary uppercase tracking-wider">Version History</h2>
                    <span className="text-xs bg-muted px-2 py-1 rounded">
                        {loading ? 'Metrics Loading...' : `${epochs.length} Iterations Tracked`}
                    </span>
                </div>
                <EpochTimeline
                    epochs={epochs}
                    currentEpochId={currentEpochId}
                    onEpochClick={handleEpochClick}
                />
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <RPNEvolutionChart
                    data={epochs.map(e => ({
                        epoch: e.epochNumber,
                        date: e.epochDate,
                        totalRPN: e.totalRPN,
                        avgRPN: e.avgRPN,
                        name: e.epochName
                    }))}
                />
                <CostEvolutionChart
                    data={epochs.map(e => ({
                        epoch: e.epochNumber,
                        date: e.epochDate,
                        totalCost: e.totalCost,
                        name: e.epochName
                    }))}
                />
            </div>

            {/* Comparison Table */}
            <div className="space-y-2">
                <h2 className="text-lg font-semibold text-text-primary">Detailed Comparison</h2>
                <EpochComparisonTable comparison={comparisonData} onEpochClick={handleEpochClick} onDelete={deleteEpoch} />
            </div>

            {/* V4 Pipeline Insights Dashboard */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* RTM Coverage */}
                <div className="bg-card border border-border rounded-lg p-4 shadow-sm">
                    <h3 className="text-sm font-semibold text-text-secondary uppercase tracking-wider mb-3 flex items-center gap-2">
                        <CheckSquare size={16} className="text-blue-500" /> Requirement Traceability
                    </h3>
                    {allData.requirement_trace_matrix?.hr_sr_table?.length > 0 ? (
                        <div className="space-y-2">
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-text-primary">Requirements Mapped</span>
                                <span className="text-lg font-bold text-blue-500">{allData.requirement_trace_matrix.hr_sr_table.length}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-text-primary">Parameter Conflicts</span>
                                <span className={`text-lg font-bold ${(allData.requirement_trace_matrix.parameter_conflicts?.length || 0) > 0 ? 'text-amber-500' : 'text-green-500'}`}>
                                    {allData.requirement_trace_matrix.parameter_conflicts?.length || 0}
                                </span>
                            </div>
                            <div className="mt-2 text-xs text-text-secondary">
                                {allData.requirement_trace_matrix.hr_sr_table.slice(0, 3).map((r: any, i: number) => (
                                    <div key={i} className="flex gap-2 py-1 border-t border-border">
                                        <span className="font-mono text-blue-400">{r.id}</span>
                                        <span className="truncate">{r.text?.substring(0, 40)}...</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <p className="text-sm text-text-secondary italic">No RTM data yet. Run a V4 synthesis to generate.</p>
                    )}
                </div>

                {/* FMEA + FTA Stats */}
                <div className="bg-card border border-border rounded-lg p-4 shadow-sm">
                    <h3 className="text-sm font-semibold text-text-secondary uppercase tracking-wider mb-3 flex items-center gap-2">
                        <AlertTriangle size={16} className="text-amber-500" /> Failure Analysis
                    </h3>
                    <div className="space-y-2">
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-text-primary">FMEA Items</span>
                            <span className="text-lg font-bold text-amber-500">{allData.fmeaItems?.length || 0}</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-text-primary">Fault Trees (FTA)</span>
                            <span className="text-lg font-bold text-orange-500">{allData.fault_trees?.length || 0}</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-text-primary">Optimization Proposals</span>
                            <span className="text-lg font-bold text-green-500">{allData.optimization_proposals?.length || 0}</span>
                        </div>
                        {allData.optimization_proposals?.length > 0 && (
                            <div className="mt-2 text-xs text-text-secondary">
                                {allData.optimization_proposals.slice(0, 2).map((o: any, i: number) => (
                                    <div key={i} className="py-1 border-t border-border">
                                        <span className="text-green-400">{o.actionType}</span>: {o.proposedAction?.substring(0, 50)}...
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* DVP&R Test Coverage */}
                <div className="bg-card border border-border rounded-lg p-4 shadow-sm">
                    <h3 className="text-sm font-semibold text-text-secondary uppercase tracking-wider mb-3 flex items-center gap-2">
                        <CheckSquare size={16} className="text-emerald-500" /> DVP&R Test Plan
                    </h3>
                    {allData.dvp_report?.test_plan?.length > 0 ? (
                        <div className="space-y-2">
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-text-primary">Verification Tests</span>
                                <span className="text-lg font-bold text-emerald-500">{allData.dvp_report.test_plan.length}</span>
                            </div>
                            {allData.dvp_report.coverage_summary && (
                                <>
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-text-primary">Requirements Covered</span>
                                        <span className="text-lg font-bold text-emerald-400">{allData.dvp_report.coverage_summary.requirements_covered || '—'}</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-text-primary">Est. Duration</span>
                                        <span className="text-sm font-bold text-text-primary">{allData.dvp_report.coverage_summary.estimated_test_duration_days || '—'} days</span>
                                    </div>
                                </>
                            )}
                            <div className="mt-2 text-xs text-text-secondary">
                                {allData.dvp_report.test_plan.slice(0, 3).map((t: any, i: number) => (
                                    <div key={i} className="flex gap-2 py-1 border-t border-border">
                                        <span className="font-mono text-emerald-400">{t.test_id}</span>
                                        <span className="truncate">{t.test_name?.substring(0, 35)}</span>
                                        <span className="ml-auto text-text-secondary">{t.test_type}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <p className="text-sm text-text-secondary italic">No DVP&R data yet. Run a V4 synthesis to generate.</p>
                    )}
                </div>

                {/* Semantic RSM Gap */}
                <div className="bg-card border border-border rounded-lg p-4 shadow-sm">
                    <h3 className="text-sm font-semibold text-text-secondary uppercase tracking-wider mb-3 flex items-center gap-2">
                        <Sparkles size={16} className="text-purple-500" /> Semantic Gap (RSM)
                    </h3>
                    {allData.semantic_gap_snapshot ? (
                        <div className="space-y-2">
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-text-primary">Gap Energy</span>
                                <span className={`text-lg font-bold ${(allData.semantic_gap_snapshot.gap_energy || 0) < 0.3 ? 'text-green-500' : 'text-amber-500'}`}>
                                    {(allData.semantic_gap_snapshot.gap_energy || 0).toFixed(4)}
                                </span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-text-primary">Requirements Mapped</span>
                                <span className="text-sm font-bold text-text-primary">{allData.semantic_gap_snapshot.n_requirements || 0}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-text-primary">Design Entities</span>
                                <span className="text-sm font-bold text-text-primary">{allData.semantic_gap_snapshot.n_design_entities || 0}</span>
                            </div>
                            {allData.semantic_gap_snapshot.top_gaps?.length > 0 && (
                                <div className="mt-2 text-xs text-text-secondary">
                                    <p className="font-bold text-amber-400 mb-1">Top Deficiency Axes:</p>
                                    {allData.semantic_gap_snapshot.top_gaps.slice(0, 3).map((g: any, i: number) => (
                                        <div key={i} className="py-1 border-t border-border flex justify-between">
                                            <span className="truncate flex-1">{g.semantic_direction?.substring(0, 50)}</span>
                                            <span className="font-mono text-amber-400 ml-2">{g.weight_pct}%</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ) : (
                        <p className="text-sm text-text-secondary italic">No semantic RSM data yet.</p>
                    )}
                </div>

                {/* Electrical Topology */}
                <div className="bg-card border border-border rounded-lg p-4 shadow-sm">
                    <h3 className="text-sm font-semibold text-text-secondary uppercase tracking-wider mb-3 flex items-center gap-2">
                        <Zap size={16} className="text-yellow-500" /> Electrical Topology
                    </h3>
                    {allData.electrical_topology?.connector_manifest?.length > 0 ? (
                        <div className="space-y-2">
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-text-primary">Connectors Defined</span>
                                <span className="text-lg font-bold text-yellow-500">{allData.electrical_topology.connector_manifest.length}</span>
                            </div>
                            <div className="mt-2 text-xs text-text-secondary">
                                {allData.electrical_topology.connector_manifest.slice(0, 3).map((c: any, i: number) => (
                                    <div key={i} className="py-1 border-t border-border flex justify-between">
                                        <span className="font-mono text-yellow-400">{c.id || c.part_number}</span>
                                        <span>{c.ip_rating || ''}</span>
                                        <span>{c.pins?.length || 0} pins</span>
                                    </div>
                                ))}
                            </div>
                            {allData.electrical_topology.harness_strategy && (
                                <p className="text-xs text-text-secondary mt-2 italic">{allData.electrical_topology.harness_strategy.substring(0, 80)}...</p>
                            )}
                        </div>
                    ) : (
                        <p className="text-sm text-text-secondary italic">No electrical data yet.</p>
                    )}
                </div>

                {/* Evolution Insight (Dynamic) */}
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                    <h3 className="text-sm font-semibold text-blue-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                        <AlertCircle size={16} /> Evolution Insight
                    </h3>
                    <p className="text-sm text-text-primary">
                        {epochs.length > 0 ? (
                            <>
                                SIPOC V4 tracking <strong>{epochs.length}</strong> iterations.
                                Latest epoch <em>{epochs[epochs.length - 1]?.epochName}</em> shows{' '}
                                {epochs.length > 1 && epochs[epochs.length - 1].totalRPN < epochs[epochs.length - 2].totalRPN
                                    ? <span className="text-green-500 font-bold">improvement ↓</span>
                                    : <span className="text-amber-500 font-bold">change →</span>
                                } from previous.
                                {allData.semantic_gap_trajectory?.length > 0 && (
                                    <> Semantic gap trajectory: <strong>{allData.semantic_gap_trajectory.length}</strong> epochs recorded.</>
                                )}
                            </>
                        ) : (
                            'No epochs recorded yet. Start a V4 synthesis to begin evolutionary descent.'
                        )}
                    </p>
                </div>
            </div>

            {/* Create Epoch Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 animate-in fade-in duration-200">
                    <div className="bg-card border border-border p-6 rounded-lg w-full max-w-md shadow-xl">
                        <h3 className="text-xl font-bold mb-4">Create New Epoch</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Epoch Name</label>
                                <input
                                    className="w-full bg-background border border-border rounded p-2"
                                    placeholder="e.g., FMEA Review - Post-Test"
                                    value={newEpochName}
                                    onChange={e => setNewEpochName(e.target.value)}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Phase</label>
                                <select
                                    className="w-full bg-background border border-border rounded p-2"
                                    value={newEpochPhase}
                                    onChange={e => setNewEpochPhase(e.target.value)}
                                >
                                    <option value="Concept">Concept</option>
                                    <option value="Design">Design</option>
                                    <option value="Validation">Validation</option>
                                    <option value="Production">Production</option>
                                </select>
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-6">
                            <button
                                onClick={() => setShowCreateModal(false)}
                                className="px-4 py-2 hover:bg-muted rounded"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleCreateEpoch}
                                disabled={!newEpochName}
                                className="px-4 py-2 bg-primary text-white rounded hover:bg-primary/90 disabled:opacity-50"
                            >
                                Create Epoch
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* File Manager Modal */}
            {showFileModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 animate-in fade-in duration-200">
                    <div className="bg-white border border-gray-200 rounded-lg shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden">
                        <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                            <h3 className="text-lg font-bold flex items-center gap-2 text-gray-800"><RefreshCw size={18} /> Manage Saved Sessions</h3>
                            <button onClick={() => setShowFileModal(false)} className="text-gray-400 hover:text-gray-800 text-2xl leading-none">&times;</button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-white">
                            {fileList.length === 0 ? (
                                <p className="text-gray-500 text-center py-8">No saved session files found.</p>
                            ) : (
                                fileList.map((f: string) => (
                                    <div key={f} className="flex items-center justify-between p-3 bg-gray-50 border border-gray-100 rounded hover:border-blue-200 hover:shadow-sm transition-all">
                                        <div className="flex items-center gap-2 overflow-hidden flex-1">
                                            <span className="truncate font-medium text-sm text-gray-700" title={f}>{f.replace('.json', '')}</span>
                                        </div>
                                        <div className="flex gap-2 shrink-0">
                                            <button
                                                onClick={() => loadFile(f)}
                                                className="bg-blue-50 text-blue-600 border border-blue-200 px-3 py-1 rounded text-xs font-bold hover:bg-blue-100 flex items-center gap-1"
                                            >
                                                Load
                                            </button>
                                            <button
                                                onClick={() => deleteFile(f)}
                                                className="bg-red-50 text-red-600 border border-red-200 px-3 py-1 rounded text-xs font-bold hover:bg-red-100 hover:text-red-700 flex items-center gap-1"
                                            >
                                                <Trash2 size={12} /> Delete
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                        <div className="p-3 border-t border-gray-100 flex justify-end bg-gray-50">
                            <button onClick={() => setShowFileModal(false)} className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded shadow-sm hover:bg-gray-100 font-medium text-sm">Close</button>
                        </div>
                    </div>
                </div>
            )}
            {/* Synthesis Sessions Modal — Gaps 3 & 5 */}
            {showSynthesisModal && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 animate-in fade-in duration-200">
                    <div className="bg-white dark:bg-slate-900 border border-border rounded-xl shadow-2xl w-full max-w-4xl max-h-[88vh] flex flex-col overflow-hidden">
                        <div className="p-4 border-b border-border flex justify-between items-center bg-slate-50 dark:bg-slate-800">
                            <h3 className="text-lg font-bold flex items-center gap-2 text-text-primary">
                                <Download size={18} className="text-emerald-500" /> Synthesis Library
                                <span className="ml-2 text-sm font-normal text-text-secondary">({synthesisSessions.length} sessions)</span>
                            </h3>
                            <button onClick={() => setShowSynthesisModal(false)} className="text-text-secondary hover:text-text-primary text-2xl leading-none">&times;</button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-white dark:bg-slate-900">
                            {synthesisSessions.length === 0 ? (
                                <p className="text-text-secondary text-center py-12">No synthesis sessions found.<br/>Run a V4 synthesis in Magnus Genesis Studio first.</p>
                            ) : synthesisSessions.map((s: any) => (
                                <div key={s.synthesis_id} className="border border-border rounded-lg p-4 hover:border-emerald-500/40 hover:shadow-sm transition-all">
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex-1 min-w-0">
                                            <p className="font-bold text-text-primary truncate">{s.project_name}</p>
                                            <p className="text-xs text-text-secondary font-mono mt-0.5">{s.synthesis_id}</p>
                                            <div className="flex flex-wrap gap-2 mt-2">
                                                <span className="text-xs px-2 py-0.5 bg-slate-100 dark:bg-slate-800 rounded">Epoch {s.current_epoch}/{s.total_epochs}</span>
                                                <span className="text-xs px-2 py-0.5 bg-slate-100 dark:bg-slate-800 rounded">{s.fmea_count} FMEA items</span>
                                                <span className="text-xs px-2 py-0.5 bg-slate-100 dark:bg-slate-800 rounded">{s.file_size_kb} KB</span>
                                                {s.has_rtm && <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">RTM ✓</span>}
                                                {s.has_fta && <span className="text-xs px-2 py-0.5 bg-orange-100 text-orange-700 rounded">FTA ✓</span>}
                                                {s.has_dvp && <span className="text-xs px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded">DVP&R ✓</span>}
                                                {s.gap_energy != null && (
                                                    <span className={`text-xs px-2 py-0.5 rounded ${s.gap_energy < 0.3 ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                                                        Gap: {s.gap_energy.toFixed(3)} {s.gap_energy < 0.3 ? '✅' : '⚠️'}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex flex-col gap-2 shrink-0">
                                            {/* GAP 5: Download Brief */}
                                            <button
                                                onClick={() => downloadBrief(s.synthesis_id, s.project_name)}
                                                className="flex items-center gap-1 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-text-primary border border-border rounded text-xs font-medium"
                                                title="Download synthesis_brief_full.json"
                                            >
                                                <Download size={12} /> Brief JSON
                                            </button>
                                            {/* Full synthesis report (Mermaid + all V4 data) */}
                                            <button
                                                onClick={() => viewSynthesisReport(s.synthesis_id, s.project_name)}
                                                className="flex items-center gap-1 px-3 py-1.5 bg-violet-50 hover:bg-violet-100 text-violet-700 border border-violet-200 rounded text-xs font-medium"
                                                title="Full synthesis report with Mermaid flowcharts"
                                            >
                                                <TrendingUp size={12} /> Synthesis Report
                                            </button>
                                            {/* NEW: 3D CAD Viewer */}
                                            {s.has_cad_model && (
                                                <button
                                                    onClick={() => setActiveCadSynthesisId({ id: s.synthesis_id, name: s.project_name })}
                                                    className="flex items-center gap-1 px-3 py-1.5 bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200 rounded text-xs font-medium"
                                                    title="View 3D WebGL assembly"
                                                >
                                                    <Box size={12} /> 3D Viewer
                                                </button>
                                            )}
                                            {/* GAP 5: View Audit Report */}
                                            {s.has_audit_report && (
                                                <button
                                                    onClick={() => viewAuditReport(s.synthesis_id, s.project_name)}
                                                    className="flex items-center gap-1 px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 rounded text-xs font-medium"
                                                    title="View V4 Governance Audit Report"
                                                >
                                                    <CheckSquare size={12} /> Audit Report
                                                </button>
                                            )}
                                            {/* GAP 3: Save as Seed */}
                                            <button
                                                onClick={() => saveAsSeed(s.synthesis_id, s.project_name)}
                                                disabled={savingSeed === s.synthesis_id}
                                                className="flex items-center gap-1 px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 rounded text-xs font-medium disabled:opacity-60"
                                                title="Save as epoch seed for future synthesis"
                                            >
                                                <Sparkles size={12} /> {savingSeed === s.synthesis_id ? 'Saving...' : 'Save as Seed'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="p-3 border-t border-border flex justify-end bg-slate-50 dark:bg-slate-800">
                            <button onClick={() => setShowSynthesisModal(false)} className="px-4 py-2 bg-white dark:bg-slate-700 border border-border text-text-primary rounded shadow-sm hover:bg-slate-100 font-medium text-sm">Close</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Audit Report Viewer — Gap 5 */}
            {showAuditModal && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 animate-in fade-in duration-200">
                    <div className="bg-white dark:bg-slate-900 border border-border rounded-xl shadow-2xl w-full max-w-3xl max-h-[88vh] flex flex-col overflow-hidden">
                        <div className="p-4 border-b border-border flex justify-between items-center bg-slate-50 dark:bg-slate-800">
                            <h3 className="text-lg font-bold flex items-center gap-2 text-text-primary">
                                <CheckSquare size={18} className="text-blue-500" /> {auditReportTitle}
                            </h3>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => {
                                        const blob = new Blob([auditReportMd], { type: 'text/markdown' });
                                        const url = URL.createObjectURL(blob);
                                        const a = document.createElement('a');
                                        a.href = url;
                                        a.download = `${auditReportTitle.replace(/[^a-z0-9]/gi, '_')}.md`;
                                        a.click();
                                        URL.revokeObjectURL(url);
                                    }}
                                    className="flex items-center gap-1 px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 rounded text-xs font-medium"
                                >
                                    <Download size={12} /> Download .md
                                </button>
                                <button onClick={() => setShowAuditModal(false)} className="text-text-secondary hover:text-text-primary text-2xl leading-none">&times;</button>
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto p-6 bg-white dark:bg-slate-900">
                            <pre className="whitespace-pre-wrap text-sm text-text-primary font-mono leading-relaxed">{auditReportMd}</pre>
                        </div>
                    </div>
                </div>
            )}

            {/* 3D WebGL CAD Viewer Modal */}
            <CadViewerModal
                isOpen={!!activeCadSynthesisId}
                onClose={() => setActiveCadSynthesisId(null)}
                synthesisId={activeCadSynthesisId?.id || ''}
                projectName={activeCadSynthesisId?.name || ''}
            />

        </div>
    );
};

export default EpochManager;
